import React, { FC, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import {
    AddLeadConnectIntegrationMutationVariables,
    useAddLeadConnectAddOnMutation,
    useAddLeadConnectIntegrationMutation,
    useLeadConnectStatusQuery,
    useLeadConnectTestCallMutation,
    useRemoveLeadConnectAddOnMutation,
} from "generated/graphql";
import { addNotification } from "layouts/AppLayout/modules";
import { wrapUrqlMutation } from "utils/graphql";

import { LeadConnectDialog } from "../components/LeadConnectDialog";
import {
    defaultLeadConnectForm,
    getLeadConnectCardState,
    leadConnectTestCallMessage,
} from "../components/leadConnectForm";

export interface LeadConnectDialogContainerProps {
    appId: string;
    organizationId: string;
    active: boolean;
    onClose(): void;
    /** The integration or the add-on changed: the integrations page should refetch. */
    onChanged?(): void;
}

/**
 * The Lead Connect dialog's data. Mount it only while the dialog is open: its query reads the
 * number live from Lead Connect, and pausing a query on open state is what froze the nav drawer.
 *
 * The add-on and the number come from leadConnectStatus rather than integrationStatus, so an API
 * that lags those fields fails this dialog alone, not the whole integrations page.
 */
export const LeadConnectDialogContainer: FC<LeadConnectDialogContainerProps> = ({
    appId,
    organizationId,
    active,
    onClose,
    onChanged,
}) => {
    const dispatch = useDispatch();

    const [status, reexecuteStatus] = useLeadConnectStatusQuery({
        requestPolicy: "cache-and-network",
        variables: { appId },
    });

    const [purchaseState, purchaseMutation] = useAddLeadConnectAddOnMutation();
    const purchase = wrapUrqlMutation([purchaseState, purchaseMutation]);

    const [setupState, setupMutation] = useAddLeadConnectIntegrationMutation();
    const setup = wrapUrqlMutation([setupState, setupMutation], {
        // A fallback (a number from a nearby town) comes back as a warning; it rides on the toast.
        onSuccessMessage: (data) => {
            const warnings = data?.app?.update?.integration?.addLeadConnectIntegration?.warnings;
            return {
                send: true,
                message: warnings?.length ? `Lead Connect saved. ${warnings.join(" ")}` : "Lead Connect saved",
            };
        },
    });

    const [testState, testMutation] = useLeadConnectTestCallMutation();
    const testCall = wrapUrqlMutation([testState, testMutation]);

    const [removeState, removeMutation] = useRemoveLeadConnectAddOnMutation();
    const remove = wrapUrqlMutation([removeState, removeMutation], {
        onSuccessMessage: "Lead Connect cancelled. Your number is kept for 30 days.",
    });

    const app = status.data?.app;
    const integration = app?.leadconnect;
    const state = getLeadConnectCardState(app?.addOns?.leadConnect?.status, !!integration?.isLinked);

    // Memoized on the query's data: the dialog restarts its form whenever the defaults change.
    const defaults = useMemo(() => defaultLeadConnectForm(app || {}), [app]);

    const refresh = useCallback(() => {
        reexecuteStatus({ requestPolicy: "network-only" });
        if (typeof onChanged === "function") {
            onChanged();
        }
    }, [reexecuteStatus, onChanged]);

    const handlePurchase = useCallback(async () => {
        // Checkout comes back to the integrations page either way.
        const returnPath = `/${organizationId}/${appId}/settings/integrations`;
        const response = await purchase({ appId, successPath: returnPath, cancelPath: returnPath });
        const result = response?.data?.app?.update?.subscriptions?.addLeadConnect;
        if (result?.result === "CHECKOUT" && result.checkoutUrl) {
            window.location.assign(result.checkoutUrl);
        } else if (result?.result === "ADDED") {
            dispatch(addNotification("Lead Connect added. Setup opens once the payment confirms."));
            refresh();
        }
    }, [appId, organizationId, purchase, dispatch, refresh]);

    const handleSetup = useCallback(
        async (variables: Omit<AddLeadConnectIntegrationMutationVariables, "appId">) => {
            const response = await setup({ appId, ...variables });
            if (!response?.error) {
                refresh();
            }
        },
        [appId, setup, refresh]
    );

    const handleTestCall = useCallback(async () => {
        const response = await testCall({ appId });
        const result = response?.data?.app?.update?.integration?.leadConnectTestCall;
        if (result) {
            const { message, isError } = leadConnectTestCallMessage(result);
            dispatch(addNotification(message, isError));
        }
    }, [appId, testCall, dispatch]);

    const handleCancelAddOn = useCallback(async () => {
        const response = await remove({ appId });
        if (!response?.error) {
            refresh();
        }
    }, [appId, remove, refresh]);

    return (
        <LeadConnectDialog
            active={active}
            state={state}
            loading={!status.data && status.fetching}
            defaults={defaults}
            number={integration?.leadConnectNumber}
            purchasing={purchaseState?.fetching}
            saving={setupState?.fetching}
            testing={testState?.fetching}
            cancelling={removeState?.fetching}
            onClose={onClose}
            onPurchase={handlePurchase}
            onSetup={handleSetup}
            onTestCall={handleTestCall}
            onCancelAddOn={handleCancelAddOn}
        />
    );
};
